import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarViewWeekOutlinedIcon from "@mui/icons-material/CalendarViewWeekOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PublicIcon from "@mui/icons-material/Public";
import PunchClockOutlinedIcon from "@mui/icons-material/PunchClockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import {
  deleteSignageDevice,
  formatBytes,
  formatRelative,
  formatUptime,
  playlistScheduleState,
  registerSignageDevice,
  renameSignageDevice,
  sendSignageCommand,
  setSignageDeviceBoard,
  setSignageDeviceMode,
  setSignageDevicePlaylists,
} from "./signageApi";
import {
  EmptyState,
  ErrorNote,
  ICON_BTN_SM,
  ICON_BTN_SM_DANGER,
  INPUT,
  Loading,
  Pill,
  ScheduleChip,
  SearchInput,
  Segmented,
  StatusDot,
  deviceIssues,
} from "./signageUi";

// Displays tab: every TV the viewer can manage, grouped by branch, with a
// fleet summary on top and a side drawer holding everything about one
// display (what it plays, remote control, health).

// The remote actions a display offers; the Pi executes the whitelist in
// rotator/telemetry.py. `confirm` gates the disruptive ones.
const DEVICE_COMMANDS = [
  {
    type: "identify",
    label: "Identify",
    icon: <FlashOnOutlinedIcon fontSize="small" />,
    hint: "Flashes the PIN on the TV",
    confirm: null,
  },
  {
    type: "refresh",
    label: "Refresh content",
    icon: <RefreshIcon fontSize="small" />,
    hint: "Re-checks for new media now",
    confirm: null,
  },
  {
    type: "restart",
    label: "Restart app",
    icon: <RestartAltIcon fontSize="small" />,
    hint: "Restarts the player software",
    confirm: (d) => `Restart the media loop on "${d.name}"?`,
  },
  {
    type: "reboot",
    label: "Reboot",
    icon: <PowerSettingsNewIcon fontSize="small" />,
    hint: "Screen goes dark for about a minute",
    confirm: (d) =>
      `Reboot "${d.name}"? The screen will go dark for about a minute.`,
  },
  {
    type: "update",
    label: "Update software",
    icon: <SystemUpdateAltIcon fontSize="small" />,
    hint: "Installs the latest player now",
    confirm: (d) =>
      `Update the player software on "${d.name}" now? Outdated players update themselves automatically — this just skips the wait.`,
  },
];

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "attention", label: "Needs attention" },
  { key: "offline", label: "Offline" },
  { key: "online", label: "Online" },
];

const matchesStatus = (device, status, playlistById) => {
  if (status === "online") return device.online;
  if (status === "offline") return !device.online;
  if (status === "attention") {
    return deviceIssues(device, playlistById).some((i) => i.attention);
  }
  return true;
};

const checkInLabel = (device) => {
  if (!device.lastSeenAt) return "Never connected";
  if (device.online) return `Checked in ${formatRelative(device.lastSeenAt)}`;
  return `Offline · last seen ${formatRelative(device.lastSeenAt)}`;
};

// A playlist a display can be given: its own branch's, or a company one.
const playableOn = (playlist, device) =>
  playlist.company || playlist.branch === device.branch;

// What's on a screen, in words: the live slides by name, company files
// without their "~id" suffix, and nothing for the hashed cache names older
// players (before v1.7.2) reported.
const friendlyFile = (name) => {
  if (!name || name === "live-board") return name === "live-board" ? "Live shop board" : "";
  if (/^weather-\(server\)/.test(name)) return "Weather";
  if (/^pcw-\(server\)/.test(name)) return "On the Clock";
  if (/^[0-9a-f]{16}\.[a-z0-9]+$/i.test(name)) return "";
  return name.replace(/~[0-9a-z]+(?=\.[^.]+$)/i, "");
};

// ================= Displays panel =================

const DisplaysPanel = ({
  branches,
  devices,
  playlists,
  userId,
  isFullAccess,
  branchFilter,
  onBranchFilter,
  openDeviceId,
  onOpenDevice,
  onOpenPlaylist,
  canOpenPlaylist,
  onGoToBranches,
  onChanged,
}) => {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [registerFor, setRegisterFor] = useState(null); // null | branch id | ""
  const [collapsed, setCollapsed] = useState(() => new Set());

  const playlistById = useMemo(
    () => new Map(playlists.map((p) => [p.id, p])),
    [playlists]
  );

  if (!devices) return <Loading />;

  if (branches.length === 0) {
    return (
      <EmptyState
        icon={<TvOutlinedIcon />}
        title="No signage branches yet"
        action={
          isFullAccess && (
            <Button variant="contained" onClick={onGoToBranches}>
              Set up a branch
            </Button>
          )
        }
      >
        {isFullAccess
          ? "Displays belong to a branch. Create one on the Branches tab, then register its TVs here."
          : "No signage branch is set up for your Big C branch yet — ask an admin to create one."}
      </EmptyState>
    );
  }

  const inScope = branchFilter
    ? devices.filter((d) => d.branch === branchFilter)
    : devices;
  const counts = {
    all: inScope.length,
    online: inScope.filter((d) => d.online).length,
    offline: inScope.filter((d) => !d.online).length,
    attention: inScope.filter((d) => matchesStatus(d, "attention", playlistById)).length,
  };

  const query = search.trim().toLowerCase();
  const branchName = new Map(branches.map((b) => [b.id, b.name]));
  const visible = inScope.filter(
    (d) =>
      matchesStatus(d, status, playlistById) &&
      (!query ||
        d.name.toLowerCase().includes(query) ||
        d.pin.includes(query) ||
        (branchName.get(d.branch) || "").toLowerCase().includes(query))
  );
  const filtering = status !== "all" || !!query;

  const groups = branches
    .filter((b) => !branchFilter || b.id === branchFilter)
    .map((branch) => ({
      branch,
      all: devices.filter((d) => d.branch === branch.id),
      shown: visible.filter((d) => d.branch === branch.id),
    }))
    // While filtering, hide branches with no matches; otherwise keep empty
    // branches so they show their "register a display" prompt.
    .filter((g) => !filtering || g.shown.length > 0);

  const openDevice = devices.find((d) => d.id === openDeviceId) || null;
  const toggleGroup = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Online / needs-attention / offline share of the fleet, as one bar.
  const healthy = inScope.filter((d) => d.online && !matchesStatus(d, "attention", playlistById)).length;
  const warnOnline = counts.online - healthy;
  const pct = (n) => (counts.all ? (n / counts.all) * 100 : 0);

  return (
    <div className="space-y-4">
      {/* Summary + filters */}
      <div className="app-card space-y-4 rounded-2xl border p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-semibold tabular-nums">
              {counts.all} display{counts.all === 1 ? "" : "s"}
              {branchFilter ? ` at ${branchName.get(branchFilter)}` : ""}
              <span className="app-muted text-sm font-normal">
                {" "}· {counts.online} online
                {counts.offline > 0 && ` · ${counts.offline} offline`}
              </span>
            </p>
            {counts.all > 0 && (
              <div
                className="mt-2.5 flex h-2 w-full max-w-md overflow-hidden rounded-full bg-slate-500/15"
                role="img"
                aria-label={`${healthy} healthy, ${warnOnline} online with a warning, ${counts.offline} offline`}
              >
                <span className="bg-emerald-500" style={{ width: `${pct(healthy)}%` }} title={`${healthy} online and healthy`} />
                <span className="bg-amber-500" style={{ width: `${pct(warnOnline)}%` }} title={`${warnOnline} online with a warning`} />
                <span className="bg-red-500" style={{ width: `${pct(counts.offline)}%` }} title={`${counts.offline} offline`} />
              </div>
            )}
          </div>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRegisterFor(branchFilter || "")}
          >
            Register display
          </Button>
        </div>

        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <SearchInput
            className="flex-1"
            placeholder="Search by name, PIN, or branch"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {branches.length > 1 && (
            <select
              className={`${INPUT} lg:w-56`}
              value={branchFilter || ""}
              onChange={(e) => onBranchFilter(e.target.value || null)}
              aria-label="Branch"
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
          <Segmented
            label="Status"
            value={status}
            onChange={setStatus}
            options={STATUS_FILTERS.map((f) => {
              const n = counts[f.key];
              const alert = (f.key === "attention" || f.key === "offline") && n > 0;
              return {
                value: f.key,
                label: f.label,
                badge: (
                  <span
                    className={`min-w-[1.125rem] rounded-full px-1.5 text-center text-[10px] font-bold leading-4 tabular-nums ${
                      alert
                        ? f.key === "offline"
                          ? "bg-red-500 text-white"
                          : "bg-amber-500 text-white"
                        : "bg-slate-500/15"
                    }`}
                  >
                    {n}
                  </span>
                ),
              };
            })}
          />
        </div>
      </div>

      {groups.length === 0 && (
        <EmptyState icon={<SearchOffIcon />} title="No displays match">
          Try a different search or status filter.
        </EmptyState>
      )}

      {groups.map(({ branch, all, shown }) => {
        const groupOnline = all.filter((d) => d.online).length;
        const isCollapsed = collapsed.has(branch.id) && !filtering;
        const allUp = all.length > 0 && groupOnline === all.length;
        return (
          <section
            key={branch.id}
            className="app-card overflow-hidden rounded-2xl border"
          >
            <header className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 sm:px-5">
              <button
                type="button"
                onClick={() => toggleGroup(branch.id)}
                className="-ml-1 flex min-h-[36px] min-w-0 items-center gap-1.5 rounded-lg px-1 text-left"
                title={isCollapsed ? "Show displays" : "Hide displays"}
                aria-expanded={!isCollapsed}
              >
                {isCollapsed ? (
                  <ChevronRightIcon className="app-muted" fontSize="small" />
                ) : (
                  <ExpandMoreIcon className="app-muted" fontSize="small" />
                )}
                <h3 className="truncate font-semibold">{branch.name}</h3>
              </button>
              {all.length > 0 && (
                <Pill tone={allUp ? "emerald" : groupOnline === 0 ? "red" : "amber"}>
                  {groupOnline}/{all.length} online
                </Pill>
              )}
              {branch.pcwEnabled && <Pill tone="sky">Shop board</Pill>}
              <button
                type="button"
                className="app-btn app-btn-ghost app-btn-sm app-accent ml-auto !gap-1"
                onClick={() => setRegisterFor(branch.id)}
              >
                <AddIcon sx={{ fontSize: 16 }} /> Add display
              </button>
            </header>

            {isCollapsed ? null : shown.length === 0 ? (
              <p className="app-divider app-muted border-t px-4 py-5 text-sm sm:px-5">
                No displays here yet. Plug in the Pi, note the PIN it shows on
                the TV, and click Add display.
              </p>
            ) : (
              <>
                <div className="app-divider app-surface app-eyebrow hidden border-t px-5 py-2 md:grid md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)] md:gap-4">
                  <span>Display</span>
                  <span>Plays</span>
                  <span>On screen</span>
                  <span className="text-right">Status</span>
                </div>
                <ul className="app-divider divide-y divide-[color:var(--app-divider)] border-t">
                  {shown.map((device) => (
                    <DeviceRow
                      key={device.id}
                      device={device}
                      playlistById={playlistById}
                      active={device.id === openDeviceId}
                      onOpen={() => onOpenDevice(device.id)}
                    />
                  ))}
                </ul>
              </>
            )}
          </section>
        );
      })}

      {openDevice && (
        <DeviceDrawer
          device={openDevice}
          branch={branches.find((b) => b.id === openDevice.branch)}
          playlists={playlists.filter((p) => playableOn(p, openDevice))}
          playlistById={playlistById}
          userId={userId}
          onClose={() => onOpenDevice(null)}
          onOpenPlaylist={onOpenPlaylist}
          canOpenPlaylist={canOpenPlaylist}
          onChanged={onChanged}
        />
      )}

      {registerFor !== null && (
        <RegisterDeviceDialog
          branches={branches}
          defaultBranchId={registerFor || branches[0]?.id}
          userId={userId}
          onClose={() => setRegisterFor(null)}
          onDone={(device) => {
            setRegisterFor(null);
            onChanged();
            onOpenDevice(device.id); // straight on to picking its playlists
          }}
        />
      )}
    </div>
  );
};

const DeviceRow = ({ device, playlistById, active, onOpen }) => {
  const issues = deviceIssues(device, playlistById);
  const warnings = issues.filter((i) => i.key !== "offline" && i.key !== "never");
  const assigned = (device.playlists || [])
    .map((id) => playlistById.get(id))
    .filter(Boolean);
  const nowShowing = device.online ? friendlyFile(device.health?.currentFile) : "";

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={`grid w-full grid-cols-1 gap-2 px-4 py-3 text-left transition sm:px-5 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)] md:items-center md:gap-4 ${
          active ? "bg-sky-500/10" : "hover:bg-[color:var(--app-hover)]"
        }`}
      >
        <div className="flex min-w-0 items-center gap-3">
          <StatusDot device={device} />
          <div className="min-w-0">
            <p className="truncate font-medium">{device.name}</p>
            <p className="app-muted truncate text-xs">{checkInLabel(device)}</p>
          </div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {device.mode === "board" ? (
            <Pill tone="sky">Live shop board</Pill>
          ) : assigned.length === 0 ? (
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              Nothing assigned
            </span>
          ) : (
            assigned.map((p) => (
              <span
                key={p.id}
                className={`app-chip max-w-[11rem] ${
                  playlistScheduleState(p) === "running" ? "" : "opacity-60"
                }`}
                title={p.company ? `${p.name} (company-wide)` : p.name}
              >
                {p.company && <PublicIcon sx={{ fontSize: 12 }} />}
                <span className="truncate">{p.name}</span>
              </span>
            ))
          )}
        </div>

        <p className="app-muted hidden min-w-0 truncate text-xs md:block" title={nowShowing}>
          {nowShowing ? (
            <>
              <PlayArrowIcon sx={{ fontSize: 13 }} className="-mt-0.5 mr-0.5 text-emerald-500" />
              {nowShowing}
            </>
          ) : device.online ? (
            "—"
          ) : (
            ""
          )}
        </p>

        <div className="flex flex-wrap items-center gap-1.5 md:justify-end">
          {!device.lastSeenAt ? (
            <Pill tone="slate">Never connected</Pill>
          ) : !device.online ? (
            <Pill tone="red">Offline</Pill>
          ) : warnings.length === 0 ? (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> OK
            </span>
          ) : null}
          {warnings.map((i) => (
            <Pill key={i.key} tone={i.tone}>
              {i.label}
            </Pill>
          ))}
          <ChevronRightIcon className="app-muted hidden md:block" fontSize="small" />
        </div>
      </button>
    </li>
  );
};

// ================= Device drawer =================

// The live shop board's screens, in the order the TV shows them. Mirrors
// BOARD_SCREENS in server/routes/api/Signage/signageShared.js. This Week and
// Deadlines skip themselves on the TV while they'd be empty.
const BOARD_SCREENS = [
  {
    field: "clockSeconds",
    label: "On the Clock",
    hint: "Who's punched in, on which job, for how long",
    icon: <PunchClockOutlinedIcon fontSize="small" />,
    defaultSeconds: 30,
  },
  {
    field: "jobsSeconds",
    label: "Job Hours",
    hint: "Total time on each open job, and who's on it now",
    icon: <WorkHistoryOutlinedIcon fontSize="small" />,
    defaultSeconds: 20,
  },
  {
    field: "weekSeconds",
    label: "This Week",
    hint: "Who is scheduled on which job (the dashboard's Schedule tab)",
    icon: <CalendarViewWeekOutlinedIcon fontSize="small" />,
    defaultSeconds: 20,
  },
  {
    field: "deadlinesSeconds",
    label: "Deadlines",
    hint: "Jobs with a due date, counting down",
    icon: <EventOutlinedIcon fontSize="small" />,
    defaultSeconds: 20,
  },
];
const BOARD_MIN_SECONDS = 5;
const BOARD_MAX_SECONDS = 3600;

// Per-screen on/off + seconds for a display on the live shop board. Edits are
// a draft until Save (a half-typed number shouldn't reach the TV).
const BoardScreens = ({ board, onSave }) => {
  // Keyed on the values, not the object — the device list refreshes on a
  // timer and must not wipe a half-typed edit.
  const savedKey = BOARD_SCREENS.map((screen) => board?.[screen.field] ?? "").join("|");
  const saved = useMemo(() => {
    const value = {};
    BOARD_SCREENS.forEach((screen) => {
      const seconds = board?.[screen.field];
      value[screen.field] = Number.isInteger(seconds) ? seconds : screen.defaultSeconds;
    });
    return value;
  }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Draft per screen: { on, seconds } with seconds kept as typed.
  const toDraft = (value) =>
    Object.fromEntries(
      BOARD_SCREENS.map((screen) => [
        screen.field,
        {
          on: value[screen.field] > 0,
          seconds: String(value[screen.field] || screen.defaultSeconds),
        },
      ])
    );
  const [draft, setDraft] = useState(() => toDraft(saved));
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(toDraft(saved)), [saved]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = {};
  BOARD_SCREENS.forEach((screen) => {
    const entry = draft[screen.field];
    result[screen.field] = entry.on ? Number(entry.seconds) : 0;
  });
  const anyOn = BOARD_SCREENS.some((screen) => draft[screen.field].on);
  const badSeconds = BOARD_SCREENS.some((screen) => {
    const seconds = result[screen.field];
    return (
      draft[screen.field].on &&
      (!Number.isInteger(seconds) ||
        seconds < BOARD_MIN_SECONDS ||
        seconds > BOARD_MAX_SECONDS)
    );
  });
  const dirty = BOARD_SCREENS.some(
    (screen) => result[screen.field] !== saved[screen.field]
  );

  const update = (field, changes) =>
    setDraft((current) => ({
      ...current,
      [field]: { ...current[field], ...changes },
    }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(result);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="app-surface divide-y divide-[color:var(--app-surface-border)] rounded-xl border">
        {BOARD_SCREENS.map((screen) => {
          const entry = draft[screen.field];
          return (
            <div key={screen.field} className="flex items-center gap-3 px-3 py-2.5">
              <span
                className={`app-icon-tile h-8 w-8 shrink-0 rounded-lg transition ${
                  entry.on
                    ? "bg-sky-500/15 text-sky-600 dark:text-sky-300"
                    : "app-muted bg-slate-500/10"
                }`}
              >
                {screen.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{screen.label}</span>
                <span className="app-muted block text-xs">{screen.hint}</span>
              </span>
              {entry.on && (
                <label className="flex shrink-0 items-center gap-1.5">
                  <input
                    className={`${INPUT} !w-20 text-right tabular-nums`}
                    type="number"
                    min={BOARD_MIN_SECONDS}
                    max={BOARD_MAX_SECONDS}
                    step="1"
                    aria-label={`${screen.label} seconds on screen`}
                    value={entry.seconds}
                    onChange={(e) => update(screen.field, { seconds: e.target.value })}
                  />
                  <span className="app-muted text-xs">sec</span>
                </label>
              )}
              <Switch
                size="small"
                checked={entry.on}
                inputProps={{ "aria-label": `Show ${screen.label}` }}
                onChange={(e) => update(screen.field, { on: e.target.checked })}
              />
            </div>
          );
        })}
      </div>
      {!anyOn ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Leave at least one screen on.
        </p>
      ) : badSeconds ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Use whole seconds from {BOARD_MIN_SECONDS} to {BOARD_MAX_SECONDS}.
        </p>
      ) : (
        <p className="app-muted text-xs">
          The TV rotates through the screens that are on, each for its seconds.
          A screen with more rows than fit pages through them, each page for
          that long.
        </p>
      )}
      {dirty && (
        <div className="flex justify-end gap-2">
          <Button size="small" onClick={() => setDraft(toDraft(saved))}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!anyOn || badSeconds || saving}
            onClick={save}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

const DrawerSection = ({ title, children, aside }) => (
  <section className="app-divider space-y-3 rounded-2xl border p-4">
    <div className="flex items-center justify-between gap-2">
      <h4 className="app-eyebrow">{title}</h4>
      {aside}
    </div>
    {children}
  </section>
);

const HealthTile = ({ label, value, warn, title }) => (
  <div className="min-w-0" title={title || String(value)}>
    <p className="app-muted truncate text-xs">{label}</p>
    <p
      className={`truncate text-sm font-semibold tabular-nums ${
        warn ? "text-amber-600 dark:text-amber-400" : ""
      }`}
    >
      {value}
    </p>
  </div>
);

const DeviceDrawer = ({
  device,
  branch,
  playlists,
  playlistById,
  userId,
  onClose,
  onOpenPlaylist,
  canOpenPlaylist,
  onChanged,
}) => {
  const [error, setError] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const issues = deviceIssues(device, playlistById);
  const health = device.health;

  const run = async (fn) => {
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  const sendCommand = (cmd) => {
    if (cmd.confirm && !window.confirm(cmd.confirm(device))) return;
    run(() => sendSignageCommand(device.id, cmd.type, userId));
  };

  const remove = async () => {
    if (
      !window.confirm(
        `Remove "${device.name}"? The TV goes back to showing its PIN until it's registered again.`
      )
    )
      return;
    try {
      await deleteSignageDevice(device.id, userId);
      onClose();
      onChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  const commandActive =
    device.command && ["pending", "sent"].includes(device.command.status);

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
    >
      <div className="flex h-full flex-col">
        <div className="app-divider flex items-start gap-3 border-b px-5 py-4">
          <span className="app-icon-tile mt-0.5 h-10 w-10 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
            <TvOutlinedIcon fontSize="small" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <h3 className="truncate text-lg font-semibold tracking-tight">{device.name}</h3>
              <button
                type="button"
                className={ICON_BTN_SM}
                title="Rename display"
                aria-label="Rename display"
                onClick={() => setRenaming(true)}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <Pill tone={device.online ? "emerald" : device.lastSeenAt ? "red" : "slate"}>
                <StatusDot device={device} />
                {device.online ? "Online" : device.lastSeenAt ? "Offline" : "Never connected"}
              </Pill>
              <span className="app-muted">{branch?.name || "—"}</span>
              <span className="app-muted">
                · PIN <span className="font-mono">{device.pin}</span>
              </span>
              {device.lastSeenAt && (
                <span className="app-muted">· seen {formatRelative(device.lastSeenAt)}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="app-icon-btn -mr-2 -mt-1"
            title="Close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <ErrorNote>{error}</ErrorNote>

          {issues.length > 0 && (
            <div className="divide-y divide-amber-500/20 overflow-hidden rounded-2xl border border-amber-500/30 bg-amber-500/10">
              {issues.map((i) => (
                <div key={i.key} className="flex gap-2.5 px-4 py-3">
                  <div className="shrink-0 pt-0.5">
                    <Pill tone={i.tone}>{i.label}</Pill>
                  </div>
                  <p className="text-xs leading-relaxed">{i.detail}</p>
                </div>
              ))}
            </div>
          )}

          {device.online && friendlyFile(health?.currentFile) && (
            <div className="flex items-center gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3">
              <span className="app-icon-tile h-9 w-9 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
                <PlayArrowIcon fontSize="small" />
              </span>
              <div className="min-w-0">
                <p className="app-eyebrow">On screen now</p>
                <p className="truncate text-sm font-medium" title={health.currentFile}>
                  {friendlyFile(health.currentFile)}
                </p>
              </div>
            </div>
          )}

          <DrawerSection title="What it plays">
            {branch?.pcwEnabled && (
              <Segmented
                fullWidth
                size="md"
                label="What it plays"
                value={device.mode || "playlist"}
                onChange={(value) =>
                  value !== (device.mode || "playlist") &&
                  run(() => setSignageDeviceMode(device.id, value, userId))
                }
                options={[
                  { value: "playlist", label: "Media playlists" },
                  { value: "board", label: "Live shop board" },
                ]}
              />
            )}
            {device.mode === "board" ? (
              <>
                <p className="app-muted text-sm">
                  Showing the live shop board full-time, rotating through the
                  screens below. Switch back to Media playlists to play slides
                  again — the display changes over within a minute.
                </p>
                <BoardScreens
                  board={device.board}
                  onSave={(board) =>
                    run(() => setSignageDeviceBoard(device.id, board, userId))
                  }
                />
              </>
            ) : (
              <DevicePlaylists
                device={device}
                playlists={playlists}
                playlistById={playlistById}
                onOpenPlaylist={onOpenPlaylist}
                canOpenPlaylist={canOpenPlaylist}
                onChange={(ids) =>
                  run(() => setSignageDevicePlaylists(device.id, ids, userId))
                }
              />
            )}
          </DrawerSection>

          <DrawerSection
            title="Remote control"
            aside={
              device.command && device.command.status !== "done" ? (
                <span
                  className={`text-xs font-medium ${
                    device.command.status === "failed"
                      ? "text-red-600 dark:text-red-400"
                      : "app-muted"
                  }`}
                  title={device.command.output || ""}
                >
                  {device.command.type}: {device.command.status}
                  {device.command.status === "pending" && " (next check-in)"}
                </span>
              ) : null
            }
          >
            <div className="grid grid-cols-2 gap-2 min-[400px]:grid-cols-3">
              {DEVICE_COMMANDS.map((cmd) => (
                <button
                  key={cmd.type}
                  type="button"
                  disabled={
                    commandActive ||
                    (cmd.type === "update" && !device.updateAvailable)
                  }
                  onClick={() => sendCommand(cmd)}
                  className="app-surface flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2.5 text-center transition hover:border-sky-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[color:var(--app-surface-border)]"
                  title={
                    commandActive
                      ? "Wait for the current action to finish"
                      : cmd.type === "update" && !device.updateAvailable
                        ? "Already on the newest version its server has"
                        : cmd.hint
                  }
                >
                  <span className="app-accent">{cmd.icon}</span>
                  <span className="text-xs font-medium leading-tight">{cmd.label}</span>
                </button>
              ))}
            </div>
            <p className="app-muted text-xs">
              Reaches the display within about 30 seconds. Hover a button for
              what it does.
            </p>
          </DrawerSection>

          <DrawerSection title="Health">
            {health ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-3 min-[400px]:grid-cols-3">
                <HealthTile
                  label="Temperature"
                  value={health.tempC != null ? `${health.tempC}°C` : "—"}
                  warn={health.tempC != null && health.tempC >= 75}
                />
                <HealthTile
                  label="Free storage"
                  value={
                    health.diskFreeMb != null
                      ? formatBytes(health.diskFreeMb * 1024 * 1024)
                      : "—"
                  }
                />
                <HealthTile
                  label="Uptime"
                  value={
                    health.uptimeSec != null ? formatUptime(health.uptimeSec) : "—"
                  }
                />
                <HealthTile
                  label={health.ssid ? `Wi-Fi · ${health.ssid}` : "Network"}
                  value={health.ip || "—"}
                  title={health.ssid ? `Wi-Fi: ${health.ssid}` : "Wired"}
                />
                <HealthTile
                  label="Player software"
                  value={
                    health.version
                      ? `v${health.version}${
                          device.updateAvailable
                            ? ` → v${device.latestClientVersion}`
                            : ""
                        }`
                      : "—"
                  }
                  warn={device.updateAvailable}
                />
                <HealthTile
                  label="Last report"
                  value={formatRelative(health.reportedAt)}
                />
              </div>
            ) : (
              <p className="app-muted text-sm">
                No health report yet — it arrives once the display is online.
              </p>
            )}
          </DrawerSection>
        </div>

        <div className="app-divider flex items-center justify-between border-t px-5 py-3">
          <a
            className="app-muted inline-flex items-center gap-1 text-xs font-medium hover:text-sky-600 dark:hover:text-sky-400"
            href={`/api/signage/pi/${device.pin}/`}
            target="_blank"
            rel="noreferrer"
            title="Open the file list this display downloads"
          >
            Files it's serving <OpenInNewIcon sx={{ fontSize: 12 }} />
          </a>
          <Button size="small" color="error" onClick={remove}>
            Remove display
          </Button>
        </div>
      </div>

      {renaming && (
        <RenameDeviceDialog
          device={device}
          userId={userId}
          onClose={() => setRenaming(false)}
          onDone={() => {
            setRenaming(false);
            onChanged();
          }}
        />
      )}
    </Drawer>
  );
};

/**
 * The playlists one display plays, in order — the Pi runs through each one's
 * items in turn as a single loop (e.g. "Default" then "Seasonal"). Every
 * change saves immediately.
 */
const DevicePlaylists = ({
  device,
  playlists,
  playlistById,
  onOpenPlaylist,
  canOpenPlaylist,
  onChange,
}) => {
  const assigned = device.playlists || [];
  const addable = playlists.filter(
    (p) => !assigned.includes(p.id) && playlistScheduleState(p) !== "ended"
  );

  const move = (idx, dir) => {
    const next = [...assigned];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {assigned.length > 1 && (
        <p className="app-muted text-xs">
          These play together as one loop, top to bottom.
        </p>
      )}
      <div className="space-y-1.5">
        {assigned.map((id, idx) => {
          const playlist = playlistById.get(id);
          return (
            <div
              key={id}
              className="app-surface flex items-center gap-2 rounded-xl border py-1.5 pl-3 pr-1.5 text-sm"
            >
              <span className="app-muted w-4 text-center text-xs font-semibold tabular-nums">
                {idx + 1}
              </span>
              {playlist && canOpenPlaylist(playlist) ? (
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left font-medium hover:text-sky-600 hover:underline dark:hover:text-sky-400"
                  title="Open this playlist"
                  onClick={() => onOpenPlaylist(playlist)}
                >
                  {playlist.name}
                </button>
              ) : (
                <span className="min-w-0 flex-1 truncate">
                  {playlist?.name ?? "(loading…)"}
                </span>
              )}
              {playlist?.company && (
                <PublicIcon
                  className="app-muted"
                  sx={{ fontSize: 14 }}
                  titleAccess="Company-wide playlist"
                />
              )}
              {playlist && <ScheduleChip playlist={playlist} />}
              <div className="flex items-center">
                <button
                  type="button"
                  className={ICON_BTN_SM}
                  title="Play earlier"
                  aria-label="Play earlier"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                </button>
                <button
                  type="button"
                  className={ICON_BTN_SM}
                  title="Play later"
                  aria-label="Play later"
                  disabled={idx === assigned.length - 1}
                  onClick={() => move(idx, 1)}
                >
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                </button>
                <button
                  type="button"
                  className={ICON_BTN_SM_DANGER}
                  title="Take off this display"
                  aria-label="Take off this display"
                  onClick={() => onChange(assigned.filter((x) => x !== id))}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {addable.length > 0 ? (
        <FormControl size="small" fullWidth>
          <Select
            value=""
            displayEmpty
            onChange={(e) =>
              e.target.value && onChange([...assigned, e.target.value])
            }
            renderValue={() => (
              <span className="app-muted text-sm">+ Add playlist</span>
            )}
          >
            {addable.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <span className="flex w-full items-center gap-2">
                  <span className="flex-1 truncate">{p.name}</span>
                  {p.company && (
                    <span className="app-muted text-xs">Company-wide</span>
                  )}
                  <ScheduleChip playlist={p} />
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        assigned.length === 0 && (
          <p className="app-muted text-sm">
            No playlists yet — build one on the Playlists tab first.
          </p>
        )
      )}
    </div>
  );
};

// ================= Dialogs =================

const RegisterDeviceDialog = ({
  branches,
  defaultBranchId,
  userId,
  onClose,
  onDone,
}) => {
  const [branchId, setBranchId] = useState(defaultBranchId || "");
  const [pin, setPin] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const device = await registerSignageDevice(
        branchId,
        { pin: pin.trim(), name },
        userId
      );
      onDone(device);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit} className="space-y-4 p-6">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Register a display</h3>
          <p className="app-muted mt-1 text-sm">
            A new Pi shows its PIN full-screen on the TV until it's registered.
          </p>
        </div>
        {branches.length > 1 ? (
          <FormControl fullWidth size="small">
            <InputLabel id="register-branch">Branch</InputLabel>
            <Select
              labelId="register-branch"
              label="Branch"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <p className="text-sm">
            <span className="app-muted">Branch: </span>
            {branches[0]?.name}
          </p>
        )}
        <TextField
          label="PIN shown on the TV (XXXX-XXXX)"
          placeholder="1234-5678"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          fullWidth
          size="small"
          autoFocus
          inputProps={{ style: { fontFamily: "monospace" } }}
        />
        <TextField
          label="Display name"
          placeholder="Front counter TV"
          helperText="Something people will recognize — where the TV is."
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
        />
        <ErrorNote>{error}</ErrorNote>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !branchId || !pin.trim() || !name.trim()}
          >
            Register
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

const RenameDeviceDialog = ({ device, userId, onClose, onDone }) => {
  const [name, setName] = useState(device.name);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await renameSignageDevice(device.id, name, userId);
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit} className="space-y-4 p-6">
        <h3 className="text-lg font-semibold tracking-tight">Rename display</h3>
        <TextField
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          autoFocus
        />
        <ErrorNote>{error}</ErrorNote>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !name.trim()}
          >
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default DisplaysPanel;
